import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { formatDate } from '@/lib/format';
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import {
  subscribeCrossTenantInvoicesByStatus,
  type CrossTenantInvoicesResult,
} from '@/lib/firestoreTenantDetail';
import { AdminListUnpaidSalesOutput, type AdminUnpaidSale } from '@etiquetachef/types';
import { adminListUnpaidSalesCallable } from '@/lib/callables';
import { reportNonFatal } from '@/lib/errorReport';

/**
 * Tela `/billing/pending` (rota padrão de `/billing`) — o que falta receber:
 * totais por moeda, vendas assistidas sem pagamento, faturas vencidas e
 * pendentes de todas as empresas.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 (Sprint 16, rotas
 * `/billing/*`).
 *
 * Contrato: duas assinaturas em tempo real de
 * `subscribeCrossTenantInvoicesByStatus` (`OVERDUE` e `PENDING`) e o
 * `UnpaidSalesContainer` (callable `adminListUnpaidSales`). Totais somados no
 * cliente por moeda, em centavos (`amount_due`). Só leitura; o número da
 * fatura leva ao detalhe dela e o tenant da venda, ao detalhe do tenant.
 *
 * Usado por: rota `billing/pending` (`routes/router.tsx`, também destino de
 * `/billing`), menu Cobrança (`AdminLayout`). Spec:
 * `docs/screens/billing-pending.md`.
 *
 * Dados: collection group `invoices` (`tenants/{tid}/invoices/{id}`), com
 * `where('status','==',X) + orderBy('due_at','asc')` — índice
 * `COLLECTION_GROUP status ASC + due_at ASC` em `firestore.indexes.json`;
 * autorizada pela rule `match /{path=**}/invoices/{invoiceId}` (só
 * super-admin). Valores em CENTAVOS (`MoneyDisplay` converte).
 *
 * Arquitetura: fatura e assinatura são escritas só pelo servidor (webhooks e
 * `dailyBillingTick`); a tela não decide nada de cobrança.
 */
export function PendingPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<CrossTenantInvoicesResult>({ kind: 'LOADING' });
  const [overdue, setOverdue] = useState<CrossTenantInvoicesResult>({ kind: 'LOADING' });

  useEffect(() => {
    setPending({ kind: 'LOADING' });
    return subscribeCrossTenantInvoicesByStatus('PENDING', setPending);
  }, []);

  useEffect(() => {
    setOverdue({ kind: 'LOADING' });
    return subscribeCrossTenantInvoicesByStatus('OVERDUE', setOverdue);
  }, []);

  const totals = useMemo(() => {
    const sumByCurrency = (list: CrossTenantInvoicesResult): Record<string, number> => {
      if (list.kind !== 'READY') return {};
      const acc: Record<string, number> = {};
      for (const inv of list.invoices) {
        acc[inv.currency] = (acc[inv.currency] ?? 0) + inv.amount_due;
      }
      return acc;
    };
    return { pending: sumByCurrency(pending), overdue: sumByCurrency(overdue) };
  }, [pending, overdue]);

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('billing.pending.subtitle')}>
          {t('billing.pending.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={2}>
          <TotalsCard
            title={t('billing.pending.totalPending')}
            totals={totals.pending}
            locale={i18n.language}
          />
          <TotalsCard
            title={t('billing.pending.totalOverdue')}
            totals={totals.overdue}
            locale={i18n.language}
          />
        </ColumnLayout>

        <UnpaidSalesContainer locale={i18n.language} />

        <Container header={<Header variant="h2">{t('billing.pending.overdueTitle')}</Header>}>
          <InvoicesByStatus list={overdue} locale={i18n.language} />
        </Container>

        <Container header={<Header variant="h2">{t('billing.pending.pendingTitle')}</Header>}>
          <InvoicesByStatus list={pending} locale={i18n.language} />
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
}

/** Card de total por moeda (soma em centavos; `—` quando vazio). */
function TotalsCard({
  title,
  totals,
  locale,
}: {
  title: string;
  totals: Record<string, number>;
  locale: string;
}): React.JSX.Element {
  const entries = Object.entries(totals);
  return (
    <Container header={<Header variant="h3">{title}</Header>}>
      {entries.length === 0 ? (
        <Box variant="h1">—</Box>
      ) : (
        <SpaceBetween size="xs">
          {entries.map(([currency, amount]) => (
            <Box key={currency} variant="h2">
              <MoneyDisplay
                amount={amount}
                currency={currency as 'BRL' | 'USD' | 'EUR'}
                locale={locale}
              />
            </Box>
          ))}
        </SpaceBetween>
      )}
    </Container>
  );
}

type CrossInvoice = Extract<CrossTenantInvoicesResult, { kind: 'READY' }>['invoices'][number];

/** Tabela de faturas cross-tenant de um status, com estados de carga e erro. */
function InvoicesByStatus({
  list,
  locale,
}: {
  list: CrossTenantInvoicesResult;
  locale: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (list.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="m">
        <Spinner />
      </Box>
    );
  }
  if (list.kind === 'ERROR') {
    return <Alert type="error">{t('billing.pending.errorLoad')}</Alert>;
  }

  return (
    <Table<CrossInvoice>
      items={list.invoices}
      trackBy="id"
      variant="embedded"
      empty={<Box textAlign="center" color="inherit">{t('billing.pending.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'invoiceNumber',
          header: t('billing.invoices.columns.invoiceNumber'),
          cell: (inv): React.ReactNode => {
            const href = `/billing/invoices/${inv.tenant_id}/${inv.id}`;
            return (
              <Link
                href={href}
                onFollow={(e): void => {
                  e.preventDefault();
                  void navigate(href);
                }}
              >
                {inv.invoice_number}
              </Link>
            );
          },
        },
        {
          id: 'tenant',
          header: t('billing.pending.columns.tenant'),
          cell: (inv): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{inv.tenant_id}</span>
          ),
        },
        {
          id: 'dueAt',
          header: t('billing.invoices.columns.dueAt'),
          cell: (inv): React.ReactNode => formatDate(inv.due_at, locale),
        },
        {
          id: 'total',
          header: t('billing.invoices.columns.total'),
          cell: (inv): React.ReactNode => (
            <MoneyDisplay amount={inv.amount_due} currency={inv.currency} locale={locale} />
          ),
        },
        {
          id: 'status',
          header: t('billing.invoices.columns.status'),
          cell: (inv): React.ReactNode => <InvoiceStatusBadge status={inv.status} />,
        },
      ]}
    />
  );
}

type UnpaidSalesState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; sales: AdminUnpaidSale[] }
  | { kind: 'ERROR' };

/**
 * #679 9.7 — vendas assistidas sem pagamento há mais de 5 dias. Só lista: a
 * exclusão é manual, pelo detalhe do tenant (decisão do owner). A lista vem
 * da callable `adminListUnpaidSales`, que usa o MESMO predicado do aviso
 * diário do `dailyBillingTick` — a tela e o e-mail nunca discordam.
 *
 * Origem: #700 — commit aeb97e006, 2026-09-22 (#679 9.7, fila de venda sem
 * pagamento).
 *
 * Contrato: resposta validada com `AdminListUnpaidSalesOutput`; fora do
 * contrato ou falha → `reportNonFatal('admin_unpaid_sales_load_failed')` e
 * estado de erro. Exportado só para o teste isolá-lo.
 *
 * Usado por: `PendingPage` (acima) e `PendingPage.test.tsx`.
 */
export function UnpaidSalesContainer({ locale }: { locale: string }): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<UnpaidSalesState>({ kind: 'LOADING' });

  useEffect(() => {
    let alive = true;
    adminListUnpaidSalesCallable({})
      .then((raw) => {
        const parsed = AdminListUnpaidSalesOutput.safeParse(raw);
        if (!parsed.success) throw new Error('adminListUnpaidSales: resposta fora do contrato');
        if (alive) setState({ kind: 'READY', sales: parsed.data.sales });
      })
      .catch((err: unknown) => {
        reportNonFatal('admin_unpaid_sales_load_failed', {
          message: err instanceof Error ? err.message : String(err),
        });
        if (alive) setState({ kind: 'ERROR' });
      });
    return (): void => {
      alive = false;
    };
  }, []);

  return (
    <Container
      header={
        <Header variant="h2" description={t('billing.pending.unpaidSales.description')}>
          {t('billing.pending.unpaidSales.title')}
        </Header>
      }
    >
      {state.kind === 'LOADING' ? (
        <Box textAlign="center" padding="m">
          <Spinner />
        </Box>
      ) : state.kind === 'ERROR' ? (
        <Alert type="error">{t('billing.pending.unpaidSales.errorLoad')}</Alert>
      ) : (
        <Table<AdminUnpaidSale>
          items={state.sales}
          trackBy="tenantId"
          variant="embedded"
          empty={
            <Box textAlign="center" color="inherit">
              {t('billing.pending.unpaidSales.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'tenant',
              header: t('billing.pending.unpaidSales.columns.tenant'),
              cell: (sale): React.ReactNode => {
                const href = `/tenants/${sale.tenantId}`;
                return (
                  <Link
                    href={href}
                    onFollow={(e): void => {
                      e.preventDefault();
                      void navigate(href);
                    }}
                  >
                    {sale.tenantName}
                  </Link>
                );
              },
            },
            {
              id: 'createdAt',
              header: t('billing.pending.unpaidSales.columns.createdAt'),
              cell: (sale): React.ReactNode => formatDate(sale.createdAt, locale),
            },
            {
              id: 'total',
              header: t('billing.pending.unpaidSales.columns.total'),
              cell: (sale): React.ReactNode =>
                sale.amountCents !== null && sale.currency ? (
                  <MoneyDisplay
                    amount={sale.amountCents}
                    currency={sale.currency as 'BRL' | 'USD' | 'EUR'}
                    locale={locale}
                  />
                ) : (
                  '—'
                ),
            },
            {
              id: 'lead',
              header: t('billing.pending.unpaidSales.columns.lead'),
              cell: (sale): React.ReactNode => (
                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{sale.leadId}</span>
              ),
            },
          ]}
        />
      )}
    </Container>
  );
}
